import { JsonObject, JsonProperty } from '@shared/convert/convert-core';

@JsonObject('ArticleNumberLong')
export class ArticleNumberLong {
  private _reducedArticleNumberLong: string;
  private _extendedArticleNumberLong: string;

  public constructor(
    articleNumberLong?: string
  ) {
    if (articleNumberLong) {
      const calculatedArticleNumbers = this.createConvertedArticleNumbers(articleNumberLong);
      this._reducedArticleNumberLong = calculatedArticleNumbers[0];
      this._extendedArticleNumberLong = calculatedArticleNumbers[1];
    } else {
      this._reducedArticleNumberLong = "";
      this._extendedArticleNumberLong = "";
    }
  }
  @JsonProperty('reducedArticleNumberLong', 'String')
  public set reducedArticleNumberLong(value: string) {
    this.setExtendedAndReducedArticleNumbers(value);
  }

  @JsonProperty('extendedArticleNumberLong', 'String')
  public set extendedArticleNumberLong(value: string) {
    this.setExtendedAndReducedArticleNumbers(value);
  }

  private setExtendedAndReducedArticleNumbers(value: string):void {
    // This sounds a bit stupid, but as we never know which variant we get from the backend, we "calculate" both values again
    // And since SonarQube does not like two identical implementations here, I did this...
    if (!!value) {
      const calculatedArticleNumbers = this.createConvertedArticleNumbers(value);
      this._reducedArticleNumberLong = calculatedArticleNumbers[0];
      this._extendedArticleNumberLong = calculatedArticleNumbers[1];
    }
  }

  public get reducedArticleNumberLong() {
    return this._reducedArticleNumberLong;
  }

  public get extendedArticleNumberLong() {
    return this._extendedArticleNumberLong;
  }

  public clone(): ArticleNumberLong {
    return new ArticleNumberLong(this._reducedArticleNumberLong);
  }

  public isEmpty(): boolean {
    return !this._reducedArticleNumberLong;
  }

  public equals(otherArticleNumber: object): boolean {
    if (!(otherArticleNumber instanceof ArticleNumberLong)) {
      return false;
    }
    return otherArticleNumber.reducedArticleNumberLong === this._reducedArticleNumberLong;
  }

  private createConvertedArticleNumbers(articleNumberLong: string): string[] {
    const articleNumberMainParts = articleNumberLong.split('/');

    const reducedMainParts: string[] = [];
    const extendedMainParts: string[] = [];
    for (let mainPart of articleNumberMainParts) {
      const doReduceThisBlock = this.startsWithDigit(mainPart);
      const subParts = mainPart.split('-');
      const reducedSubParts: string[] = [];
      const extendedSubParts: string[] = [];
      for (let subPart of subParts) {
        reducedSubParts.push(doReduceThisBlock ? this.reduceLeadingZeroes(subPart) : subPart);
        extendedSubParts.push(this.expandLeadingZeroes(subPart));
      }
      reducedMainParts.push(reducedSubParts.join('-'));
      extendedMainParts.push(extendedSubParts.join('-'));
    }

    return [reducedMainParts.join('/'), extendedMainParts.join('/')];
  }

  private reduceLeadingZeroes(partNumber: string) {
    const completelyReducedPartNumber = partNumber.replace(/^0+/, "");
    return completelyReducedPartNumber === partNumber ? partNumber : completelyReducedPartNumber.padStart(3, '0');
  }

  private expandLeadingZeroes(partNumber: string) {
    if (this.startsWithDigit(partNumber)) {
      return partNumber.padStart(4, '0');
    }
    return partNumber;
  }

  private startsWithDigit(partNumber: string) {
    return !!partNumber.match(/^\d/);
  }
}
